import { observer } from 'mobx-react-lite'
import { match } from '@/utils/pattern-match-util'
import { BusinessInfoContainer } from '@/features/jobs/info-gathering/business-info/components/BusinessInfo/BusinessInfoContainer'
import { PersonalProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/PersonalProfile/PersonalProfileContainer'
import { SpousalProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/SpousalProfile/SpousalProfileContainer'
import { DependentsProfileContainer } from '@/features/jobs/info-gathering/individual-info/components/DependentsProfile/DependentsProfileContainer'
import { RefundInfoContainer } from '@/features/jobs/info-gathering/tax-refund-method/components/RefundInfo/RefundInfoContainer'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { ContentError } from '@/components/ContentError'
import { IntakeAnswersContainer } from '@/features/jobs/details/components/IntakeAnswers/IntakeAnswersContainer'
import { IntakeAnswersSkeleton } from '@/features/jobs/details/components/IntakeAnswers'
import { CapturedInformationLoadContainer } from '@/features/jobs/details/components/CapturedInformationLoadContainer'
import { NotesTabContainer } from '@/features/jobs/details/components/CapturedInformationCard/NotesTabContainer'
import { IntakePane } from '@/features/jobs/details/components/JobSections/IntakePane'

export interface IntakePaneContainerProps {
  vm: JobDetailsViewModel
}

export const IntakePaneContainer = observer(function IntakePaneContainer({
  vm,
}: IntakePaneContainerProps) {
  const skeleton = <IntakeAnswersSkeleton answerCount={vm.job.answers.length} />
  const renderAnswers = () => (
    <IntakeAnswersContainer
      vm={vm.detailsVm}
      onAskClient={vm.canAskClient ? vm.openChat : undefined}
    />
  )

  return (
    <IntakePane
      tabs={vm.capturedInformationVm.intakeTabs}
      currentTab={vm.capturedInformationVm.currentIntakeTab}
      onTabChange={vm.detailsVm.informationTabController.change}
      showEditResponses={vm.showDraftView}
      onEditResponses={vm.actionsVm.editDraft}
    >
      {match(vm.capturedInformationVm.currentIntakeTab, {
        Questions: () =>
          vm.detailsVm.fetchResponses.match({
            pending: () =>
              vm.detailsVm.isRevalidatingResponses ? renderAnswers() : skeleton,
            rejected: (err) => (
              <ContentError
                size="section"
                title="Error loading questions."
                error={err}
              />
            ),
            resolved: renderAnswers,
          }),
        Notes: () => (
          <CapturedInformationLoadContainer
            vm={vm.capturedInformationVm}
            skeleton={skeleton}
          >
            <NotesTabContainer vm={vm.capturedInformationVm.notesVm} />
          </CapturedInformationLoadContainer>
        ),
        PersonalInfo: () => (
          <CapturedInformationLoadContainer
            vm={vm.capturedInformationVm}
            skeleton={skeleton}
          >
            {vm.capturedInformationVm.personalProfileVm && (
              <PersonalProfileContainer
                vm={vm.capturedInformationVm.personalProfileVm}
                editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
              />
            )}
            {vm.capturedInformationVm.spousalProfileVm && (
              <SpousalProfileContainer
                vm={vm.capturedInformationVm.spousalProfileVm}
                editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
              />
            )}
            {vm.capturedInformationVm.dependentProfileVm && (
              <DependentsProfileContainer
                vm={vm.capturedInformationVm.dependentProfileVm}
                editVm={vm.capturedInformationVm.editPersonalProfileDialogVm}
              />
            )}
          </CapturedInformationLoadContainer>
        ),
        BusinessInfo: () => (
          <CapturedInformationLoadContainer
            vm={vm.capturedInformationVm}
            skeleton={skeleton}
          >
            {vm.capturedInformationVm.businessInfoVm && (
              <BusinessInfoContainer
                vm={vm.capturedInformationVm.businessInfoVm}
                editVm={vm.capturedInformationVm.editBusinessProfileDialogVm}
              />
            )}
          </CapturedInformationLoadContainer>
        ),
        RefundInfo: () => (
          <CapturedInformationLoadContainer
            vm={vm.capturedInformationVm}
            skeleton={skeleton}
          >
            {vm.capturedInformationVm.taxRefundMethodVm && (
              <RefundInfoContainer
                vm={vm.capturedInformationVm.taxRefundMethodVm}
                editVm={vm.capturedInformationVm.editRefundDialogVm}
              />
            )}
          </CapturedInformationLoadContainer>
        ),
      })}
    </IntakePane>
  )
})
